/**
 * Identifier column resolution shared by the edge functions and the client
 * (`src/shared/identifierResolution/*` re-export this module).
 */

export const STANDARD_IDENTIFIER_CANONICALS = [
  'sku',
  'asin',
  'gtin',
  'upc',
  'ean',
  'barcode',
] as const;

export type StandardIdentifierCanonical = (typeof STANDARD_IDENTIFIER_CANONICALS)[number];

/** Header aliases folded to a system standard identifier (trim + case-insensitive match). */
export const STANDARD_IDENTIFIER_ALIASES: Record<string, StandardIdentifierCanonical> = {
  sku: 'sku',
  'seller-sku': 'sku',
  'seller sku': 'sku',
  product_sku: 'sku',
  item_sku: 'sku',
  'item-sku': 'sku',
  msku: 'sku',
  merchant_sku: 'sku',
  'merchant-sku': 'sku',
  upc: 'upc',
  asin: 'asin',
  fnsku: 'asin',
  barcode: 'barcode',
  ean: 'ean',
  gtin: 'gtin',
  product_id: 'sku',
};

export interface ResolveIdentifierColumnOptions {
  customNames?: string[];
  /** Optional maps_to lookup: custom header (normalized) → canonical type. */
  customCanonicalByName?: Record<string, string>;
}

export interface IdentifierColumnResolution {
  kind: 'standard' | 'custom' | 'unsupported';
  canonicalType: string;
  rawHeader: string;
}

export function normalizeIdentifierHeader(header: string): string {
  return header.trim().toLowerCase();
}

/**
 * Standard identifiers first, then organisation custom names.
 * Matching is trim + case-insensitive. Internal spacing is preserved for custom names.
 */
export function resolveIdentifierColumn(
  header: string,
  options: ResolveIdentifierColumnOptions = {},
): IdentifierColumnResolution {
  const rawHeader = header ?? '';
  const normalized = normalizeIdentifierHeader(rawHeader);
  if (!normalized) {
    return { kind: 'unsupported', canonicalType: '', rawHeader };
  }

  const standard = STANDARD_IDENTIFIER_ALIASES[normalized];
  if (standard) {
    return { kind: 'standard', canonicalType: standard, rawHeader };
  }

  const customNames = options.customNames ?? [];
  const customMatch = customNames.find(
    (name) => normalizeIdentifierHeader(name) === normalized,
  );
  if (customMatch) {
    const mapped = options.customCanonicalByName?.[normalized];
    const canonicalType = (mapped && mapped.trim()) || customMatch.trim();
    const mappedStandard = mapped
      ? STANDARD_IDENTIFIER_ALIASES[normalizeIdentifierHeader(mapped)]
      : undefined;
    if (mappedStandard) {
      return { kind: 'standard', canonicalType: mappedStandard, rawHeader };
    }
    return { kind: 'custom', canonicalType, rawHeader };
  }

  return { kind: 'unsupported', canonicalType: '', rawHeader };
}

export function resolveIdentifierTypeValue(
  identifierType: string,
  options: ResolveIdentifierColumnOptions = {},
): IdentifierColumnResolution {
  return resolveIdentifierColumn(identifierType, options);
}

export const INVENTORY_UPLOAD_NO_SUPPORTED_IDENTIFIER =
  '/errors/inventory-upload/no-supported-identifier';
